import React, { useState } from 'react';
import { Table, TableHead, TableBody, TableRow, MenuItem, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import QueryString from 'qs';
import moment from 'moment';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import { getSmsUsageHistoryList } from '../../../api/sms';
import {
  ControlsRow,
  PageSizeGroup,
  PageSizeLabel,
  PageSizeSelect,
  SearchInput,
  TableWrapper,
  HeaderCell,
  BodyCell,
  DataRow,
  DeltaText,
  PaginationContainer,
  PageButton,
  NavButton,
  EllipsisText,
} from './SmsUsageHistoryStyled';

const COLUMNS = [
  { id: 'branchName', label: '지점명' },
  { id: 'usedAt', label: '사용일시' },
  { id: 'historyType', label: '내역구분' },
  { id: 'changeCount', label: '변동 건수' },
  { id: 'remainingCount', label: '남은 건수' },
];

const USAGE_TYPE_MAP = {
  SMS_SEND: '문자 발송',
  CHARGE: '충전',
  ADMIN_ADJUST: '관리자 조정',
  REFUND: '환불',
  SEND_FAIL_REFUND: '발송실패 복구',
};

const formatDateTime = value => {
  if (!value) return '-';
  const m = moment(value);
  return m.isValid() ? m.format('YYYY-MM-DD HH:mm') : '-';
};

const formatDelta = amount => {
  if (amount > 0) return `+${Number(amount).toLocaleString()}건`;
  if (amount < 0) return `${Number(amount).toLocaleString()}건`;
  return '0건';
};

const SmsUsageHistory = () => {
  const { t } = useTranslation();
  const { user } = useSelector(state => state.user);
  const branchId = user?.branch?.branchId;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');

  const query = QueryString.stringify({
    page: page - 1,
    size: pageSize,
  });

  const { data: historyData } = useQuery({
    queryKey: ['smsUsageHistory', branchId, page, pageSize],
    queryFn: () => getSmsUsageHistoryList(query, user?.token, branchId),
    enabled: !!user?.token && !!branchId,
  });

  const rawList = historyData?.content || [];
  const keyword = searchKeyword.trim().toLowerCase();

  const list = keyword
    ? rawList.filter(item => {
        const branchName = (user?.branch?.branchName || '').toLowerCase();
        const typeLabel = (USAGE_TYPE_MAP[item.usageType] || item.usageType || '').toLowerCase();
        const description = (item.description || '').toLowerCase();
        return branchName.includes(keyword) || typeLabel.includes(keyword) || description.includes(keyword);
      })
    : rawList;

  const totalPages = historyData?.totalPages || 1;

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      setSearchKeyword(searchValue);
      setPage(1);
    }
  };

  const renderCellValue = item => columnId => {
    switch (columnId) {
      case 'branchName':
        return user?.branch?.branchName || '-';
      case 'usedAt':
        return formatDateTime(item.registeredDate);
      case 'historyType':
        return USAGE_TYPE_MAP[item.usageType] || item.usageType || '-';
      case 'changeCount':
        return <DeltaText delta={item.amount}>{formatDelta(item.amount)}</DeltaText>;
      case 'remainingCount':
        return item.balanceAfter != null ? `${Number(item.balanceAfter).toLocaleString()}건` : '-';
      default:
        return '-';
    }
  };

  const renderPagination = () => {
    const maxVisible = 3;
    let startPage = Math.max(1, page - 1);
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) pages.push(i);

    const showLastEllipsis = endPage < totalPages - 1;
    const showLastPage = endPage < totalPages;

    return (
      <PaginationContainer>
        <NavButton onClick={() => setPage(prev => Math.max(1, prev - 1))} disabled={page === 1}>
          <KeyboardArrowLeftIcon />
        </NavButton>
        {pages.map(p => (
          <PageButton key={p} isActive={p === page} onClick={() => setPage(p)}>
            {p}
          </PageButton>
        ))}
        {showLastEllipsis && <EllipsisText>...</EllipsisText>}
        {showLastPage && (
          <PageButton isActive={page === totalPages} onClick={() => setPage(totalPages)}>
            {totalPages}
          </PageButton>
        )}
        <NavButton onClick={() => setPage(prev => Math.min(totalPages, prev + 1))} disabled={page === totalPages}>
          <KeyboardArrowRightIcon />
        </NavButton>
      </PaginationContainer>
    );
  };

  return (
    <>
      <ControlsRow>
        <PageSizeGroup>
          <PageSizeLabel>{t('페이지당 항목 수')}</PageSizeLabel>
          <PageSizeSelect
            value={pageSize}
            onChange={e => {
              setPageSize(e.target.value);
              setPage(1);
            }}>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </PageSizeSelect>
        </PageSizeGroup>

        <SearchInput
          placeholder={t('검색어를 입력해 주세요')}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon style={{ color: '#a6a6a6' }} />
              </InputAdornment>
            ),
          }}
        />
      </ControlsRow>

      <TableWrapper>
        <Table>
          <TableHead>
            <TableRow>
              {COLUMNS.map(col => (
                <HeaderCell key={col.id}>{t(col.label)}</HeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length > 0 ? (
              list.map((item, idx) => {
                const getValue = renderCellValue(item);
                return (
                  <DataRow key={item.id || idx}>
                    {COLUMNS.map(col => (
                      <BodyCell key={col.id}>{getValue(col.id)}</BodyCell>
                    ))}
                  </DataRow>
                );
              })
            ) : (
              <DataRow>
                <BodyCell colSpan={COLUMNS.length} style={{ textAlign: 'center', color: '#a6a6a6' }}>
                  {t('데이터가 없습니다')}
                </BodyCell>
              </DataRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      {renderPagination()}
    </>
  );
};

export default SmsUsageHistory;
